import { Box, useTheme } from '@mui/material';
import { FormattedMessage } from 'react-intl';

import { GooglePayButton } from '@ocx/data-payment-provider';
import { Button, SizingMultiplier } from '@ocx/ui';
import {
  applePayPaymentInstrumentPlaceholder,
  googlePayPaymentInstrumentPlaceholder,
  PaymentInstrument,
} from '@ocx/data-payment-instruments';
import { ApplePayButton } from '@ocx/cap-apple-pay';
import { PaymentAccountType } from '@ocx/graphql';

import { messages } from '../../../pay-at-pump.messages';

type PayButtonProps = {
  onGooglePayButtonClick(): void;
  onApplePayButtonClick(): void;
  onCardPayButtonClick(): void;
  paymentInstruments: PaymentInstrument[];
  selectedPaymentInstrument: PaymentInstrument | null;
  isListMode: boolean;
};

export const PayButton = ({
  onGooglePayButtonClick,
  onApplePayButtonClick,
  onCardPayButtonClick,
  paymentInstruments,
  selectedPaymentInstrument,
  // display available apple/google pay buttons as a list
  isListMode,
}: PayButtonProps) => {
  const theme = useTheme();

  if (isListMode) {
    return (
      <>
        {paymentInstruments.map((paymentInstrument) => {
          if (paymentInstrument.uuid === applePayPaymentInstrumentPlaceholder.uuid) {
            return (
              <Box pb={SizingMultiplier.md} key={paymentInstrument.uuid}>
                <ApplePayButton
                  onClick={onApplePayButtonClick}
                  variant={theme.palette.payments.applePayButtonVariant}
                />
              </Box>
            );
          }
          if (paymentInstrument.uuid === googlePayPaymentInstrumentPlaceholder.uuid) {
            return (
              <Box pb={SizingMultiplier.lg} key={paymentInstrument.uuid}>
                <GooglePayButton
                  onClick={onGooglePayButtonClick}
                  variant={theme.palette.payments.googlePayButtonVariant}
                />
              </Box>
            );
          }
          // Cards not supported with "isListMode" prop
          return null;
        })}
      </>
    );
  }

  if (selectedPaymentInstrument?.accountType === PaymentAccountType.ApplePay) {
    return <ApplePayButton onClick={onApplePayButtonClick} variant={theme.palette.payments.applePayButtonVariant} />;
  }

  if (selectedPaymentInstrument?.accountType === PaymentAccountType.GooglePay) {
    return <GooglePayButton onClick={onGooglePayButtonClick} variant={theme.palette.payments.googlePayButtonVariant} />;
  }

  const isCardSelected =
    selectedPaymentInstrument?.accountType === PaymentAccountType.Credit ||
    selectedPaymentInstrument?.accountType === PaymentAccountType.Prepaid;
  return (
    <Button disabled={!isCardSelected} onClick={isCardSelected ? onCardPayButtonClick : undefined} fullWidth>
      <FormattedMessage {...messages.payAtPumpPayWithCardButtonTitle} />
    </Button>
  );
};
